import React, { useEffect, useState } from "react";
import {
  TextField,
  FormControl,
  MenuItem,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
  InputAdornment,
} from "@mui/material";
import Button from "../../Button";
import { Controller, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import CategoryIcon from "@mui/icons-material/Category";
import ScaleIcon from "@mui/icons-material/Scale";
import { useSnackbar } from 'notistack';
import Loader from "../../loader/Loading";
import { FaSpinner } from "react-icons/fa";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { getReq, postReq } from "../../../apis/api";

const AddScrapModal = ({
  open,
  handleClose,
  ItemId = "", 
  selectedItemData={},
  onItemAdded = () => {},
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [fetchingCategories, setFetchingCategories] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const {
    control,
    reset,
    handleSubmit,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      id: "",
      type: "",
      category_id: "",
      weight: "",
      scrap_weight: "",
    },
  }); 

  const selectedType = watch("type");
  const selectedCategoryId = watch("category_id");
  // SGEW 19 Oct (field audit): scrap is always by weight - the server takes kg off the record. A Quantity item's pieces
  // were shown and typed here as its "weight" and then taken off as kg (pieces unchanged); pieces go with Scrap Pieces
  // on the Items page, so only Kilogram items are offered (the server refuses the others with the same advice)
  const amountLabel = "Weight (kg)";
  const isUpdate = Boolean(selectedItemData?.id || ItemId);
  const weight = watch("weight");
  const scrapWeight = watch("scrap_weight");

  const fetchCategories = async (type) => {
    try {
      setFetchingCategories(true);
      let endpoint = "";

      switch (type) {
        case "item":
          endpoint = "/fetchItems";
          break;
        case "subitem":
          endpoint = "/subitems";
          break;
        case "product":
          endpoint = "/products"; // every product (productInfo lists only the ones shown on the website)
          break;
        case "part": // returned parts (NEW-68) are listed for their scrap row only
        case "parts":
          endpoint = "/fetchAllParts";
          break;
        default:
          endpoint = "/fetchItems";
      }

      const response = await getReq(endpoint);

      if (response.status === 200) {
        // enabled records only; items counted in kg (a row being viewed keeps its own record in the list)
        const keep = (r) =>
          String(r.id) === String(selectedItemData?.category_id) ||
          ((!r.status || r.status === "Enable") && (type !== "item" || r.type === "Kilogram"));
        const rows = (response.data || []).filter(keep);
        setCategories(rows);
        return rows; // Return the data for chaining
      }
    } catch (error) {
      setCategories([]);
      if (error?.response?.status !== 404) enqueueSnackbar("The list could not be loaded.", { variant: "error" });
    } finally {
      setFetchingCategories(false);
    }
  };

  const fetchSplitData = async () => {
    try {
      const response = await getReq(`fetchSingleScrap/${ItemId}`);

      if (response?.status === 200 && response?.data) {
        const apiData = response?.data;

        reset({
          id: apiData?.id,
          type: apiData?.type,
          scrap_weight: apiData?.scrap_weight,
        });

        await fetchCategories(apiData.type);

        setValue("category_id", apiData.category_id);
        setValue("weight", apiData.weight);
      } else {
        enqueueSnackbar(response?.data?.message || "Failed to fetch Scrap data", { variant: "error" });
      }
    } catch (error) {
      console.error("Error fetching scrap data:", error);
      enqueueSnackbar(
        error.response?.data?.message ||
          "Something went wrong while fetching scrap data",
        { variant: "error" }
      );
    }
  };

    useEffect(() => {
    if (ItemId && open) {
      fetchSplitData();
    } else if (selectedItemData?.id && open) {  

      const initializeForm = async () => {
        if (selectedItemData.type) {
          await fetchCategories(selectedItemData.type);
        }
        
        reset({
          id: selectedItemData.id,
          type: selectedItemData.type,
          category_id: selectedItemData.category_id,
          weight: selectedItemData.weight,
          scrap_weight: selectedItemData.scrap_weight,
        });
      };
      
      initializeForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reloads only when the item, the selected row or the open state changes
  }, [ItemId, selectedItemData, open]);

  // when a row is changed, its own scrap is already off the stock shown: it may grow by up to that stock
  const scrapLimit = parseFloat(weight) + (isUpdate ? parseFloat(selectedItemData?.scrap_weight) || 0 : 0);
  const overLimit = (v) => v !== "" && v !== null && v !== undefined && Number.isFinite(scrapLimit) && parseFloat(v) > scrapLimit;
  const limitText = `Cannot be more than ${+scrapLimit.toFixed(2)} (the ${amountLabel.toLowerCase()} there is)`;
  // SGEW 21 Oct (demo filling): a figure over the weight there is stays in the box with the limit under it - the box was
  // emptied, so the save then said only "Scrap weight is required"
  useEffect(() => {
    if (overLimit(scrapWeight)) {
      setError("scrap_weight", { type: "max", message: limitText });
    } else if (errors.scrap_weight?.type === "max") {
      clearErrors("scrap_weight");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checks again when the figure or the weight there is changes
  }, [scrapWeight, scrapLimit]);

  // Update weight when category is selected
  useEffect(() => {
    if (selectedCategoryId && categories.length > 0) {
      const selectedCategory = categories.find(
        (cat) => cat.id === selectedCategoryId
      );

      if (selectedCategory) {
        let weight = "";
        switch (selectedType) {
          case "item":
            weight = selectedCategory.actual_weight_kg || "";
            break;
          case "subitem":
            weight = selectedCategory.total_weight_kg || "";
            break;
          case "product":
            weight = selectedCategory.weight || "";
            break;
          case "parts":
            weight = selectedCategory.weight || "";
            break;
          default:
            weight = "";
        }

        setValue("weight", weight); // kg in stock (a Quantity item showed its piece count)
      }
    }
  }, [selectedCategoryId, categories, selectedType, setValue]);

    useEffect(() => {
    if (selectedType) {
      fetchCategories(selectedType);
      
      // Only reset category and weight if we're not in update mode
      if (!selectedItemData?.id && !ItemId) {
        setValue("category_id", "");
        setValue("weight", "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetches only when the type or the edited record changes
  }, [selectedType, selectedItemData?.id, ItemId]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const response = await postReq(
        data,
        isUpdate ? "/updateScrap" : "/createScrap"
      );

      if (response.status === 201) {
        enqueueSnackbar(
          isUpdate
            ? response?.data?.message || "Item Scrap Updated Successfully"
            : response?.data?.message || "Scrap record created successfully",
          { variant: "success" }
        );
        reset();
        setTimeout(() => {
          onItemAdded();
          handleClose();
        }, 500);
      } else {
        enqueueSnackbar(
          response?.data?.message || "Failed to create scrap record",
          { variant: "error" }
        );
      }
    } catch (error) {
      const status = error?.response?.status;
      if (error.response?.data?.messages) {
        const errorMsg = error.response.data.messages;
        Object.values(errorMsg).forEach((message) => {
          enqueueSnackbar(message, { variant: "error" });
        });
      } else if (error.response?.data?.message && status !== 403 && status < 500) {
        // SGEW 19 Oct (field audit): the server's reason (not enough stock, already on the list...) - it showed
        // "Something went wrong"; 403 / 5xx are reported by the session interceptor
        enqueueSnackbar(error.response.data.message, { variant: "error" });
      } else if (status !== 403 && !(status >= 500)) {
        enqueueSnackbar("Something went wrong. Please try again.", { variant: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const theme = createTheme({
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiInputBase-input": {
              fontSize: "clamp(14px, 1.1vw, 1.1rem)",
            },
            "& .MuiInputLabel-root": {
              fontSize: "clamp(14px, 1.1vw, 1.1rem)",
            },
          },
        },
      },
    },
  });


  return (
    <ThemeProvider theme={theme}>
      {isLoading && <Loader />}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        sx={{
          "& .MuiDialogContent-root": {
            padding: "24px !important",
          },
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle
            className="border-b border-gray-300"
            sx={{ position: "relative" }}
          >
            <Typography variant="h6">{(selectedItemData?.id || ItemId) ? "Update Scrap" : "Add New Scrap"}</Typography>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "#6b7280",
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={4}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required variant="outlined">
                  <Controller
                    name="type"
                    control={control}
                    rules={{ required: "Type is required" }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Type"
                        fullWidth
                        disabled={isUpdate} // the source of a scrap row is fixed (the server refuses a change)
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CategoryIcon />
                            </InputAdornment>
                          ),
                        }}
                        error={!!errors.type}
                        helperText={errors.type?.message}
                      >
                        <MenuItem value="" disabled>
                          Select Type
                        </MenuItem>
                        <MenuItem value="item">Item</MenuItem>
                        <MenuItem value="subitem">Sub Item</MenuItem>
                        <MenuItem value="product">Product</MenuItem>
                        {/* NEW-72: no "Part" - parts carry no weight, and the server scraps items, sub-items and products only;
                            a returned part's scrap row (NEW-68) still shows its type */}
                        {field.value === "part" && <MenuItem value="part">Part (returned)</MenuItem>}
                      </TextField>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required variant="outlined">
                  <Controller
                    name="category_id"
                    control={control}
                    rules={{ required: "Name is required" }}
                    render={({ field }) => isUpdate ? (
                      // a scrap row keeps its source: its name as listed (a written-off or returned record may be disabled)
                      <TextField
                        label="Name"
                        fullWidth
                        disabled
                        value={selectedItemData?.note ? `${selectedItemData?.category_name || "-"} (${selectedItemData.note})` : selectedItemData?.category_name || ""}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CategoryIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    ) : (
                      <TextField
                        {...field}
                        select
                        label="Name"
                        fullWidth
                        disabled={fetchingCategories || !selectedType}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <CategoryIcon />
                            </InputAdornment>
                          ),
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                maxHeight: 250,
                              },
                            },
                          },
                        }}
                        error={!!errors.category_id}
                        helperText={errors.category_id?.message}
                      >
                        <MenuItem value="" disabled>
                          {fetchingCategories
                            ? "Loading..."
                            : "Select a category"}
                        </MenuItem>
                        {categories.map((category) => (
                          <MenuItem key={category.id} value={category.id}>
                            {category.subitem_name ||
                              category.item_name ||
                              category.product_name || category.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="weight"
                  control={control}
                  rules={{
                    required: "Weight is required",
                    min: {
                      value: 0,
                      message: "Weight must be positive",
                    },
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label={amountLabel}
                      placeholder="Enter weight"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScaleIcon />
                          </InputAdornment>
                        ),
                        readOnly: true, // Make it read-only since it's auto-populated
                      }}
                      error={!!errors.weight}
                      helperText={errors.weight?.message}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Controller
                  name="scrap_weight"
                  control={control}
                  rules={{
                    required: "Scrap weight is required",
                    min: { value: 0, message: "Scrap weight must be positive" },
                    validate: (v) => !overLimit(v) || limitText,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label={`Scrap ${amountLabel}`}
                      placeholder="Enter scrap weight/quantity"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScaleIcon />
                          </InputAdornment>
                        ),
                      }}
                      error={!!errors.scrap_weight}
                      helperText={errors.scrap_weight?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions className="md:mr-14px-vw md:mb-10px-vw">
            {/* SGEW 21 Oct: the form's submit saves once (a submit button with its own click handler saved twice) */}
            <Button
              type="submit"
              title={(selectedItemData?.id || ItemId) ? "Update Scrap" : "Add Scrap"}
              isLoading={isLoading}
              spinner={<FaSpinner />}
            />
          </DialogActions>
        </form>
      </Dialog>
    </ThemeProvider>
  );
};

export default AddScrapModal;
